/* eslint-disable unicorn/prefer-module */
import { useEffect, useState, useMemo, useCallback } from "react";
import { FaCheck } from "react-icons/fa";
import { useAuthContext } from "../../context/auth";
import { getEdificios } from "../../utils";
import { Header } from "../Header";
import { Loader } from "../Loader";
import styles from "./InventarioSimple.module.scss";

export const InventarioSimple = () => {
  const [edificios, setEdificios] = useState();
  const [sortBy, setSortBy] = useState(null);
  const [sortOrder, setSortOrder] = useState("asc");
  const [sortedColumn, setSortedColumn] = useState(null);
  const [filtroEstado, setFiltroEstado] = useState([]);
  const [filtroUbicacion, setFiltroUbicacion] = useState([]);

  const { pais } = useAuthContext();

  const customEstadoOrder = {
    OPERATIVO: 1,
    REACTIVACIÓN: 2,
    INSTALACIÓN: 3,
    STANDBY: 4,
  };

  useEffect(() => {
    const initialize = async () => {
      const newEdificios = await getEdificios({ pais });
      const sortedByEstado = [...newEdificios].sort(
        (a, b) => customEstadoOrder[a.estado] - customEstadoOrder[b.estado]
      );
      const sortedByEstadoAndPantallas = sortedByEstado.sort((a, b) => {
        if (a.estado === b.estado) {
          return b.pantallas - a.pantallas;
        }
        return 0;
      });
      const sortedByEstadoPantallasAndEdificio = sortedByEstadoAndPantallas.sort((a, b) => {
        if (a.estado === b.estado && a.pantallas === b.pantallas) {
          return a.edificio.localeCompare(b.edificio);
        }
        return 0;
      });
      setEdificios(sortedByEstadoPantallasAndEdificio);
    };
    initialize();
  }, [pais]);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("asc");
      setSortedColumn(column);
    }
  };

  const estadosUnicos = useMemo(() => {
    if (!edificios) return [];
    return [...new Set(edificios.map((e) => e.estado))].sort((a, b) => customEstadoOrder[a] - customEstadoOrder[b]);
  }, [edificios]);

  const ubicacionesUnicas = useMemo(() => {
    if (!edificios) return [];
    return [...new Set(edificios.map((e) => e.ubicacion))].sort();
  }, [edificios]);

  const filteredEdificios = useMemo(() => {
    if (!edificios) return [];
    let filtered = edificios;
    if (filtroEstado.length > 0) {
      filtered = filtered.filter((e) => filtroEstado.includes(e.estado));
    }
    if (filtroUbicacion.length > 0) {
      filtered = filtered.filter((e) => filtroUbicacion.includes(e.ubicacion));
    }
    return filtered;
  }, [edificios, filtroEstado, filtroUbicacion]);

  const sortedEdificios = useMemo(() => {
    if (!filteredEdificios) return [];
    return [...filteredEdificios].sort((a, b) => {
      if (sortBy === "pantallas" || sortBy === "personas") {
        return sortOrder === "asc" ? a[sortBy] - b[sortBy] : b[sortBy] - a[sortBy];
      }
      if (sortBy === "estado") {
        return sortOrder === "asc"
          ? customEstadoOrder[a.estado] - customEstadoOrder[b.estado]
          : customEstadoOrder[b.estado] - customEstadoOrder[a.estado];
      }
      const aValue = (a[sortBy] || "").toLowerCase();
      const bValue = (b[sortBy] || "").toLowerCase();
      return sortOrder === "asc" ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    });
  }, [filteredEdificios, sortBy, sortOrder]);

  const localString = useCallback(
    (pais) =>
      ({
        Chile: "es-CL",
        Perú: "es-PE",
        Uruguay: "es-UY",
      })[pais] || "es-PE",
    []
  );

  const calculateEmpresasCantidad = (empresaslistado) => {
    if (!empresaslistado || empresaslistado.toUpperCase() === "NO DISPONIBLE") {
      return 0;
    }
    return empresaslistado.split(",").length;
  };

  // Stats del header: solo OPERATIVOS
  const edificiosOperativos = useMemo(() => (edificios || []).filter((e) => e.estado === "OPERATIVO"), [edificios]);
  const totalEdificiosOperativos = edificiosOperativos.length;
  const totalPantallasOperativos = edificiosOperativos.reduce((sum, e) => sum + (e.pantallas || 0), 0);
  const totalAudienciaOperativos = edificiosOperativos.reduce((sum, e) => sum + (e.personas || 0), 0);
  const totalEmpresasOperativos = edificiosOperativos.reduce(
    (sum, e) => sum + calculateEmpresasCantidad(e.empresaslistado),
    0
  );

  // Totales de la tabla (edificios filtrados/mostrados)
  const totalPantallasTabla = sortedEdificios.reduce((sum, e) => sum + (e.pantallas || 0), 0);
  const totalAudienciaTabla = sortedEdificios.reduce((sum, e) => sum + (e.personas || 0), 0);

  let rowNum = 1;

  return (
    <div className={styles.inventarioSimpleTable}>
      <Header
        leftContent={
          <div className={styles.statsContainer}>
            <div className={styles.statLine}>
              Edificios: <strong>{totalEdificiosOperativos}</strong>
            </div>
            <div className={styles.statLine}>
              Audiencia:{" "}
              <strong>
                {totalAudienciaOperativos.toLocaleString(localString(pais), {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                })}
              </strong>{" "}
              personas únicas
            </div>
            <div className={styles.statLine}>
              Pantallas: <strong>{totalPantallasOperativos}</strong>
            </div>
            <div className={styles.statLine}>
              Alcance B2B: <strong>{totalEmpresasOperativos}</strong> empresas
            </div>
          </div>
        }
      >
        <div className={styles.filterRow}>
          <select
            className={styles.filterSelect}
            value={filtroEstado[0] || ""}
            onChange={(e) => setFiltroEstado(e.target.value ? [e.target.value] : [])}
          >
            <option value="">Status</option>
            {estadosUnicos.map((estado, i) => (
              <option key={i} value={estado}>
                {estado}
              </option>
            ))}
          </select>
          <select
            className={styles.filterSelect}
            value={filtroUbicacion[0] || ""}
            onChange={(e) => setFiltroUbicacion(e.target.value ? [e.target.value] : [])}
          >
            <option value="">Ubicación</option>
            {ubicacionesUnicas.map((ubicacion, i) => (
              <option key={i} value={ubicacion}>
                {ubicacion}
              </option>
            ))}
          </select>
        </div>
      </Header>

      {sortedEdificios ? (
        <div className={styles.tableContainer}>
          <table className={`${styles.table} table table-striped table-hover table-bordered table-sm`}>
            <thead className="align-middle">
              <tr>
                <th className={styles.col3}>#</th>
                <th
                  className={`${styles.col20} ${sortedColumn === "edificio" ? "sorted" : ""}`}
                  onClick={() => handleSort("edificio")}
                >
                  EDIFICIO
                  {sortedColumn === "edificio" && <span>{sortOrder === "asc" ? " ▲" : " ▼"}</span>}
                </th>
                <th
                  className={`${styles.col10} ${sortedColumn === "estado" ? "sorted" : ""}`}
                  onClick={() => handleSort("estado")}
                >
                  STATUS
                  {sortedColumn === "estado" && <span>{sortOrder === "asc" ? " ▲" : " ▼"}</span>}
                </th>
                <th
                  className={`${styles.col10} ${sortedColumn === "ubicacion" ? "sorted" : ""}`}
                  onClick={() => handleSort("ubicacion")}
                >
                  UBICACIÓN
                  {sortedColumn === "ubicacion" && <span>{sortOrder === "asc" ? " ▲" : " ▼"}</span>}
                </th>
                <th
                  className={`${styles.col18} ${sortedColumn === "direccion" ? "sorted" : ""}`}
                  onClick={() => handleSort("direccion")}
                >
                  DIRECCIÓN
                  {sortedColumn === "direccion" && <span>{sortOrder === "asc" ? " ▲" : " ▼"}</span>}
                </th>
                <th
                  className={`${styles.col8} ${sortedColumn === "programmatic" ? "sorted" : ""}`}
                  onClick={() => handleSort("programmatic")}
                >
                  PROGRAMMATIC
                  {sortedColumn === "programmatic" && <span>{sortOrder === "asc" ? " ▲" : " ▼"}</span>}
                </th>
                <th
                  className={`${styles.col10} ${sortedColumn === "personas" ? "sorted" : ""}`}
                  onClick={() => handleSort("personas")}
                >
                  PERSONAS DIARIAS
                  {sortedColumn === "personas" && <span>{sortOrder === "asc" ? " ▲" : " ▼"}</span>}
                </th>
                <th
                  className={`${styles.col8} ${sortedColumn === "pantallas" ? "sorted" : ""}`}
                  onClick={() => handleSort("pantallas")}
                >
                  PANTALLAS
                  {sortedColumn === "pantallas" && <span>{sortOrder === "asc" ? " ▲" : " ▼"}</span>}
                </th>
              </tr>
            </thead>
            <tbody className="text-center align-middle">
              {sortedEdificios.map(
                ({ id, edificio, pantallas, ubicacion, direccion, personas, estado, programatica }) => {
                  const estadoClass =
                    estado === "OPERATIVO"
                      ? styles.estadoOperativo
                      : estado === "REACTIVACIÓN"
                        ? styles.estadoReactivacion
                        : estado === "INSTALACIÓN"
                          ? styles.estadoInstalacion
                          : estado === "STANDBY"
                            ? styles.estadoStandby
                            : "";
                  return (
                    <tr key={id}>
                      <td>{rowNum++}</td>
                      <td className={styles.textStart}>{edificio.toUpperCase()}</td>
                      <td className={estadoClass}>{estado}</td>
                      <td>{ubicacion}</td>
                      <td>{direccion}</td>
                      <td>{programatica ? <FaCheck /> : "-"}</td>
                      <td>
                        {personas.toLocaleString(localString(pais), {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        })}
                      </td>
                      <td>{pantallas}</td>
                    </tr>
                  );
                }
              )}
            </tbody>
            <tfoot>
              <tr className={styles.bottom}>
                <td colSpan="6" />
                <td>
                  {totalAudienciaTabla.toLocaleString(localString(pais), {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  })}
                </td>
                <td>{totalPantallasTabla}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      ) : (
        <Loader />
      )}
    </div>
  );
};
